import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardMedia,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    Slider,
    Typography,
    useMediaQuery,
    styled,
} from "@mui/material";
import { indigo } from "@mui/material/colors";
import { PieChart } from "@mui/x-charts/PieChart";

import useEmblaCarousel from "embla-carousel-react";

import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import { collection, getDocs, query, where, getCountFromServer } from "firebase/firestore";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import RecentActorsIcon from "@mui/icons-material/RecentActors";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import PostAddIcon from "@mui/icons-material/PostAdd";
import QueuePlayNextIcon from "@mui/icons-material/QueuePlayNext";
import ListIcon from "@mui/icons-material/List";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import MoodIcon from "@mui/icons-material/Mood";
import EditIcon from "@mui/icons-material/Edit";
import TwitterIcon from "@mui/icons-material/Twitter";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const EmblaContainer = styled("div")({
    overflow: "hidden",
    width: "100%",
    position: "relative",
});

const EmblaViewport = styled("div")({
    width: "100%",
    overflow: "hidden",
});

const EmblaSlideFlex = styled("div")({
    display: "flex",
});

const EmblaSlide = styled("div")({
    flex: "0 0 100%",
    minWidth: 0,
    position: "relative",
});

const DotButton = styled("button")(({ active }) => ({
    background: "transparent",
    cursor: "pointer",
    position: "relative",
    padding: 0,
    width: "12px",
    height: "12px",
    marginRight: "8px",
    marginLeft: "8px",
    border: 0,
    display: "flex",
    alignItems: "center",
    "&::after": {
        background: active ? indigo[900] : "#efefef",
        borderRadius: "50%",
        width: "100%",
        height: "100%",
        content: '""',
    },
    "&:hover::after": {
        background: active ? indigo[900] : "#aaa",
    },
}));

const PrevNextButton = styled(IconButton)({
    zIndex: 1,
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "50%",
});

function Home() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [user, setUser] = useState("");
    const [userData, setUserData] = useState([]);
    const [charaCount, setCharaCount] = useState([]);
    const [scinaCount, setScinaCount] = useState([]);
    const [aliveChara, setAliveChara] = useState(0);
    const [deadChara, setDeadChara] = useState(0);
    const [notClearScina, setNotClearScina] = useState(0);
    const [clearScina, setClearScina] = useState(0);
    const [clearVideo, setClearVideo] = useState(0);
    const [notClearVideo, setNotClearVideo] = useState(0);
    const [releaseVideo, setReleaseVideo] = useState(0);
    const [notReleaseVideo, setNotReleaseVideo] = useState(0);
    const [aiMember, setAiMember] = useState([]);

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedIndexAi, setSelectedIndexAi] = useState(0);

    const isSP = useMediaQuery("(max-width:600px)");
    const marginTop = isSP ? 12 : 14;
    const marginBottom = isSP ? 4 : 0;
    const mtVM = isSP ? 0 : 4;
    const chartWidth = isSP ? 350 : 600;
    const chartWidth2 = isSP ? 300 : 350;
    const userWidth = isSP ? 175 : 350;
    const userHeight = isSP ? 450 : 600;
    const aiHeight = isSP ? 380 : 450;

    const fetchMembers = async (uid) => {
        try {
            // メンバー情報の取得
            const memberRef = collection(db, "member");
            const memberSnapshot = await getDocs(memberRef);

            const members = [];
            memberSnapshot.forEach((doc) => {
                members.push({
                    id: doc.data().id,
                    img: doc.data().img,
                    name: doc.data().name,
                    desc: doc.data().desc,
                    frequency: doc.data().frequency,
                    twitter: doc.data().twitter,
                    uid: doc.data().uid,
                });
            });
            members.sort((a, b) => a.id - b.id);
            setUserData(members);

            // aiメンバー情報を取得
            const aiMemberRef = collection(db, "aiMember");
            const aiMemberSnapshot = await getDocs(aiMemberRef);

            const aiMembers = [];
            aiMemberSnapshot.forEach((doc) => {
                aiMembers.push({
                    id: doc.data().id,
                    img: doc.data().img,
                    name: doc.data().name,
                    desc: doc.data().desc,
                    uid: doc.data().uid,
                });
            });
            aiMembers.sort((a, b) => a.id - b.id);
            setAiMember(aiMembers);

            // パフォーマンス改善: 並列でクエリを実行
            const charaRef = collection(db, "character");
            const scinaRef = collection(db, "scinario");
            const videoRef = collection(db, "video");

            // 1. メンバーごとのキャラクターとシナリオの件数を並列で取得
            const charaPromises = members.map(async (item) => {
                const memberQuery = query(charaRef, where("deleted", "==", false), where("auther", "==", item.uid));
                const count = await getCountFromServer(memberQuery);
                return {
                    id: item.id,
                    value: count.data().count,
                    label: item.name,
                };
            });

            const scinaPromises = members.map(async (item) => {
                const memberQuery = query(scinaRef, where("deleted", "==", false), where("auther", "==", item.uid));
                const count = await getCountFromServer(memberQuery);
                return {
                    id: item.id,
                    value: count.data().count,
                    label: item.name,
                };
            });

            // 2. 現在ユーザーの統計情報クエリを並列で実行
            const aliveQuery = query(charaRef, where("deleted", "==", false), where("auther", "==", uid), where("dead", "==", false));
            const deadQuery = query(charaRef, where("deleted", "==", false), where("auther", "==", uid), where("dead", "==", true));
            const notClearQuery = query(scinaRef, where("deleted", "==", false), where("auther", "==", uid), where("clear", "==", false));
            const clearQuery = query(scinaRef, where("deleted", "==", false), where("auther", "==", uid), where("clear", "==", true));

            // 3. ビデオ関連の統計クエリを並列で実行
            const clearVideoQuery = query(videoRef, where("completed", "==", true));
            const notClearVideoQuery = query(videoRef, where("completed", "==", false));
            const releaseVideoQuery = query(videoRef, where("release", "==", true));
            const notReleaseVideoQuery = query(videoRef, where("release", "==", false));

            // 全てのカウントクエリを並列で実行
            const [
                charaResults,
                scinaResults,
                aliveCount,
                deadCount,
                notClearCount,
                clearCount,
                clearVideoCount,
                notClearVideoCount,
                releaseVideoCount,
                notReleaseVideoCount,
            ] = await Promise.all([
                Promise.all(charaPromises),
                Promise.all(scinaPromises),
                getCountFromServer(aliveQuery),
                getCountFromServer(deadQuery),
                getCountFromServer(notClearQuery),
                getCountFromServer(clearQuery),
                getCountFromServer(clearVideoQuery),
                getCountFromServer(notClearVideoQuery),
                getCountFromServer(releaseVideoQuery),
                getCountFromServer(notReleaseVideoQuery),
            ]);

            // 結果をステートに設定
            setCharaCount(charaResults);
            setScinaCount(scinaResults);
            setAliveChara(aliveCount.data().count);
            setDeadChara(deadCount.data().count);
            setNotClearScina(notClearCount.data().count);
            setClearScina(clearCount.data().count);
            setClearVideo(clearVideoCount.data().count);
            setNotClearVideo(notClearVideoCount.data().count);
            setReleaseVideo(releaseVideoCount.data().count);
            setNotReleaseVideo(notReleaseVideoCount.data().count);
        } catch (error) {
            console.error("データ取得エラー:", error);
        } finally {
            setLoading(false);
        }
    };

    // Embla Carousel設定
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: "center",
        skipSnaps: false,
    });
    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);
    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);
    const scrollTo = useCallback(
        (index) => {
            if (emblaApi) emblaApi.scrollTo(index);
        },
        [emblaApi]
    );
    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);
    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi, onSelect]);

    const [emblaAiRef, emblaAiApi] = useEmblaCarousel({
        loop: true,
        align: "center",
        skipSnaps: false,
    });
    const scrollPrevAi = useCallback(() => {
        if (emblaAiApi) emblaAiApi.scrollPrev();
    }, [emblaAiApi]);
    const scrollNextAi = useCallback(() => {
        if (emblaAiApi) emblaAiApi.scrollNext();
    }, [emblaAiApi]);
    const scrollToAi = useCallback(
        (index) => {
            if (emblaAiApi) emblaAiApi.scrollTo(index);
        },
        [emblaAiApi]
    );
    const onSelectAi = useCallback(() => {
        if (!emblaAiApi) return;
        setSelectedIndexAi(emblaAiApi.selectedScrollSnap());
    }, [emblaAiApi]);
    useEffect(() => {
        if (!emblaAiApi) return;
        onSelectAi();
        emblaAiApi.on("select", onSelectAi);
        return () => {
            emblaAiApi.off("select", onSelectAi);
        };
    }, [emblaAiApi, onSelectAi]);

    const handleTransMylist = () => {
        window.scrollTo(0, 0);
        navigate("/mylist");
    };

    const handleTransCharaCreate = () => {
        window.scrollTo(0, 0);
        navigate("/character-create");
    };

    const handleTransCharaList = () => {
        window.scrollTo(0, 0);
        navigate("/character");
    };

    const handleTransScinarioCreate = () => {
        window.scrollTo(0, 0);
        navigate("/scinario-create");
    };
    const handleTransScinarioList = () => {
        window.scrollTo(0, 0);
        navigate("/scinario");
    };

    const handleTransVideoCreate = () => {
        window.scrollTo(0, 0);
        navigate("/video-create");
    };

    const handleTransVideoList = () => {
        window.scrollTo(0, 0);
        navigate("/video");
    };

    const handleTransTwitter = (twitter) => {
        window.open(twitter, "_blank", "noopener,noreferrer");
    };
    const handleTransEdit = (uid) => {
        window.scrollTo(0, 0);
        navigate(`/${uid}/edit`);
    };

    const handleTransAiMemberEdit = (uid) => {
        window.scrollTo(0, 0);
        navigate(`/aimember/${uid}`);
    };

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged(async (userLogin) => {
            if (userLogin) {
                const uid = userLogin.uid;
                setUser(uid);

                await fetchMembers(uid);
            } else {
                window.scrollTo(0, 0);
                navigate("/login", { replace: true });
            }
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Helmet>
                <title>SOG Member Site</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="1280" sx={{ display: { xs: { maxWidth: 0 } }, maxWidth: 1280 }}>
                <Box sx={{ mt: marginTop }}>
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mb: marginBottom }}
                        >
                            <Box style={{ position: "relative" }} sx={{ minWidth: chartWidth }}>
                                <Card>
                                    <CardHeader title="キャラクター" sx={{ textAlign: "right", mr: 2 }} />
                                    <CardContent
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <PieChart
                                                series={[
                                                    {
                                                        data: charaCount,
                                                        innerRadius: 30,
                                                        outerRadius: 60,
                                                        startAngle: 0,
                                                        endAngle: 360,
                                                    },
                                                ]}
                                                width={chartWidth2}
                                                height={180}
                                            />

                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<PersonAddAlt1Icon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransCharaCreate}
                                                >
                                                    作成
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ListIcon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransCharaList}
                                                >
                                                    リスト
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Card
                                    style={{
                                        position: "absolute",
                                        top: -15,
                                        left: 20,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    sx={{
                                        width: 100,
                                        height: 60,
                                        backgroundColor: indigo[900],
                                    }}
                                >
                                    <PeopleAltIcon sx={{ fontSize: 50 }} />
                                </Card>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mb: marginBottom }}
                        >
                            <Box style={{ position: "relative" }} sx={{ minWidth: chartWidth }}>
                                <Card>
                                    <CardHeader title="シナリオ" sx={{ textAlign: "right", mr: 2 }} />
                                    <CardContent
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <PieChart
                                                series={[
                                                    {
                                                        data: scinaCount,
                                                        innerRadius: 30,
                                                        outerRadius: 60,
                                                        startAngle: 0,
                                                        endAngle: 360,
                                                    },
                                                ]}
                                                width={chartWidth2}
                                                height={180}
                                            />
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<PostAddIcon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransScinarioCreate}
                                                >
                                                    作成
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ListIcon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransScinarioList}
                                                >
                                                    リスト
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Card
                                    style={{
                                        position: "absolute",
                                        top: -15,
                                        left: 20,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    sx={{
                                        width: 100,
                                        height: 60,
                                        backgroundColor: indigo[900],
                                    }}
                                >
                                    <DescriptionIcon sx={{ fontSize: 50 }} />
                                </Card>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mb: marginBottom, mt: mtVM }}
                        >
                            <Box style={{ position: "relative" }} sx={{ minWidth: chartWidth }}>
                                <Card>
                                    <CardHeader title="ビデオ" sx={{ textAlign: "right", mr: 2 }} />
                                    <CardContent
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Grid container>
                                                <Grid item xs={4} sm={4} sx={{ ml: 2 }}>
                                                    <Typography variant="body2">公開中</Typography>
                                                    <Typography>{releaseVideo}件</Typography>

                                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                                        完結済み
                                                    </Typography>
                                                    <Typography>{clearVideo}件</Typography>
                                                </Grid>
                                                <Grid item xs={4} sm={4} sx={{ ml: 7 }}>
                                                    <Typography variant="body2">非公開</Typography>
                                                    <Typography>{notReleaseVideo}件</Typography>
                                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                                        未完結
                                                    </Typography>
                                                    <Typography>{notClearVideo}件</Typography>
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<QueuePlayNextIcon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransVideoCreate}
                                                >
                                                    作成
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ListIcon />}
                                                    sx={{ mx: 2, width: 130 }}
                                                    onClick={handleTransVideoList}
                                                >
                                                    リスト
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Card
                                    style={{
                                        position: "absolute",
                                        top: -15,
                                        left: 20,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    sx={{
                                        width: 100,
                                        height: 60,
                                        backgroundColor: indigo[900],
                                    }}
                                >
                                    <RecentActorsIcon sx={{ fontSize: 50 }} />
                                </Card>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mb: marginBottom, mt: mtVM }}
                        >
                            <Box style={{ position: "relative" }} sx={{ minWidth: chartWidth }}>
                                <Card>
                                    <CardHeader title="マイページ" sx={{ textAlign: "right", mr: 2 }} />
                                    <CardContent
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Grid container>
                                                <Grid item xs={4} sm={4} sx={{ ml: 2 }}>
                                                    <Typography variant="body2">生存キャラ</Typography>
                                                    <Typography>{aliveChara}人</Typography>

                                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                                        未探索シナリオ
                                                    </Typography>
                                                    <Typography>{notClearScina}人</Typography>
                                                </Grid>
                                                <Grid item xs={4} sm={4} sx={{ ml: 6 }}>
                                                    <Typography variant="body2">死亡キャラ</Typography>
                                                    <Typography>{deadChara}人</Typography>
                                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                                        クリアシナリオ
                                                    </Typography>
                                                    <Typography>{clearScina}人</Typography>
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ListIcon />}
                                                    sx={{ mx: 2, width: 310 }}
                                                    onClick={handleTransMylist}
                                                >
                                                    マイリスト
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Card
                                    style={{
                                        position: "absolute",
                                        top: -15,
                                        left: 20,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                    sx={{
                                        width: 100,
                                        height: 60,
                                        backgroundColor: indigo[900],
                                    }}
                                >
                                    <RecentActorsIcon sx={{ fontSize: 50 }} />
                                </Card>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {isSP ? (
                    <Box sx={{ mb: 5, position: "relative" }}>
                        <EmblaContainer>
                            <EmblaViewport ref={emblaRef}>
                                <EmblaSlideFlex>
                                    {userData.map((item) => (
                                        <EmblaSlide key={item.id}>
                                            <Card sx={{ width: "100%", height: userHeight, display: "flex", flexDirection: "column" }}>
                                                {item.img ? (
                                                    <CardMedia component="img" height={userWidth} image={item.img} sx={{ objectFit: "contain" }} />
                                                ) : (
                                                    <Box sx={{ height: userWidth, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                        <SentimentSatisfiedAltIcon sx={{ fontSize: 150, color: "gray" }} />
                                                    </Box>
                                                )}
                                                <CardContent sx={{ flexGrow: 1 }}>
                                                    <Typography variant="h5">{item.name}</Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        style={{
                                                            overflowWrap: "break-word",
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        {item.desc}
                                                    </Typography>
                                                    <Grid container sx={{ pt: 3 }}>
                                                        <Grid item xs={6} sm={6}>
                                                            <Typography>PL</Typography>
                                                        </Grid>
                                                        <Grid item xs={6} sm={6}>
                                                            <Typography style={{ textAlign: "right" }}>KP</Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Box
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Slider
                                                            max={10}
                                                            min={0}
                                                            value={item.frequency}
                                                            sx={{
                                                                maxWidth: 300,
                                                                pointerEvents: "none",
                                                                "& .MuiSlider-thumb": {
                                                                    pointerEvents: "none",
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </CardContent>
                                                <CardActions sx={{ mt: "auto" }}>
                                                    <Grid container>
                                                        <Grid item xs={6} sm={6}>
                                                            {item.twitter !== "" && (
                                                                <IconButton size="small" onClick={() => handleTransTwitter(item.twitter)}>
                                                                    <TwitterIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Grid>
                                                        <Grid item xs={6} sm={6} textAlign="right">
                                                            {user === item.uid && (
                                                                <IconButton size="small" onClick={() => handleTransEdit(item.uid)}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </CardActions>
                                            </Card>
                                        </EmblaSlide>
                                    ))}
                                </EmblaSlideFlex>
                            </EmblaViewport>
                        </EmblaContainer>

                        {/* ナビゲーション部分をGridで構成 */}
                        <Grid container sx={{ mt: 2 }} alignItems="center" justifyContent="center">
                            <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <PrevNextButton onClick={scrollPrev} sx={{ position: "static" }} disableRipple>
                                    <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                                </PrevNextButton>
                            </Grid>

                            <Grid item xs={8} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                {userData.map((_, index) => (
                                    <DotButton key={index} active={index === selectedIndex} onClick={() => scrollTo(index)} />
                                ))}
                            </Grid>

                            <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <PrevNextButton onClick={scrollNext} sx={{ position: "static" }} disableRipple>
                                    <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                                </PrevNextButton>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2, mb: 5, mx: 1 }}>
                        <Grid container spacing={2} justifyContent="center">
                            {userData.map((item) => (
                                <Grid item xs={6} md={4} key={item.id}>
                                    <Card sx={{ width: "100%", height: userHeight, display: "flex", flexDirection: "column" }}>
                                        {item.img ? (
                                            <CardMedia component="img" height={userWidth} image={item.img} sx={{ objectFit: "contain" }} />
                                        ) : (
                                            <Box sx={{ height: userWidth, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <SentimentSatisfiedAltIcon sx={{ fontSize: 150, color: "gray" }} />
                                            </Box>
                                        )}
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography variant="h5">{item.name}</Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                style={{
                                                    overflowWrap: "break-word",
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {item.desc}
                                            </Typography>
                                            <Grid container sx={{ pt: 3 }}>
                                                <Grid item xs={6} sm={6}>
                                                    <Typography>PL</Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={6}>
                                                    <Typography style={{ textAlign: "right" }}>KP</Typography>
                                                </Grid>
                                            </Grid>
                                            <Box
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Slider
                                                    max={10}
                                                    min={0}
                                                    value={item.frequency}
                                                    sx={{
                                                        maxWidth: 300,
                                                        pointerEvents: "none",
                                                        "& .MuiSlider-thumb": {
                                                            pointerEvents: "none",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </CardContent>
                                        <CardActions sx={{ mt: "auto" }}>
                                            <Grid container>
                                                <Grid item xs={6} sm={6}>
                                                    {item.twitter !== "" && (
                                                        <IconButton size="small" onClick={() => handleTransTwitter(item.twitter)}>
                                                            <TwitterIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Grid>
                                                <Grid item xs={6} sm={6} textAlign="right">
                                                    {user === item.uid && (
                                                        <IconButton size="small" onClick={() => handleTransEdit(item.uid)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {isSP ? (
                    <Box sx={{ mb: 5, position: "relative" }}>
                        <EmblaContainer>
                            <EmblaViewport ref={emblaAiRef}>
                                <EmblaSlideFlex>
                                    {aiMember.map((item) => (
                                        <EmblaSlide key={item.id}>
                                            <Card sx={{ width: "100%", height: aiHeight, display: "flex", flexDirection: "column" }}>
                                                {item.img ? (
                                                    <CardMedia component="img" height={userWidth} image={item.img} sx={{ objectFit: "contain" }} />
                                                ) : (
                                                    <Box sx={{ height: userWidth, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                        <MoodIcon sx={{ fontSize: 150, color: "gray" }} />
                                                    </Box>
                                                )}
                                                <CardContent sx={{ flexGrow: 1 }}>
                                                    <Typography variant="h5">{item.name}</Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        style={{
                                                            overflowWrap: "break-word",
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        {item.desc}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions sx={{ mt: "auto", display: "flex", justifyContent: "flex-end" }}>
                                                    {user === "O73YrYlsmmWigCRGeq9aujj4Oxm1" && (
                                                        <IconButton size="small" onClick={() => handleTransEdit(item.uid)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </CardActions>
                                            </Card>
                                        </EmblaSlide>
                                    ))}
                                </EmblaSlideFlex>
                            </EmblaViewport>
                        </EmblaContainer>

                        {/* ナビゲーション部分をGridで構成 */}
                        <Grid container sx={{ mt: 2 }} alignItems="center" justifyContent="center">
                            <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <PrevNextButton onClick={scrollPrevAi} sx={{ position: "static" }} disableRipple>
                                    <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                                </PrevNextButton>
                            </Grid>

                            <Grid item xs={8} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                {aiMember.map((_, index) => (
                                    <DotButton key={index} active={index === selectedIndexAi} onClick={() => scrollToAi(index)} />
                                ))}
                            </Grid>

                            <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <PrevNextButton onClick={scrollNextAi} sx={{ position: "static" }} disableRipple>
                                    <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                                </PrevNextButton>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2, mb: 5, position: "relative" }}>
                        <EmblaContainer>
                            <EmblaViewport ref={emblaAiRef}>
                                <EmblaSlideFlex>
                                    {aiMember.map((item) => (
                                        <EmblaSlide key={item.id} style={{ flex: "0 0 33.333%" }}>
                                            <Box sx={{ px: 1 }}>
                                                <Card sx={{ width: "100%", height: aiHeight, display: "flex", flexDirection: "column" }}>
                                                    {item.img ? (
                                                        <CardMedia
                                                            component="img"
                                                            height={userWidth}
                                                            image={item.img}
                                                            sx={{ objectFit: "contain", mt: "auto" }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: userWidth,
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <MoodIcon sx={{ fontSize: 150, color: "gray" }} />
                                                        </Box>
                                                    )}
                                                    <CardContent sx={{ flexGrow: 1 }}>
                                                        <Typography variant="h5">{item.name}</Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            style={{
                                                                overflowWrap: "break-word",
                                                                whiteSpace: "pre-wrap",
                                                            }}
                                                        >
                                                            {item.desc}
                                                        </Typography>
                                                    </CardContent>
                                                    <CardActions sx={{ mt: "auto", display: "flex", justifyContent: "flex-end" }}>
                                                        {user === "O73YrYlsmmWigCRGeq9aujj4Oxm1" && (
                                                            <IconButton size="small" onClick={() => handleTransAiMemberEdit(item.uid)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </CardActions>
                                                </Card>
                                            </Box>
                                        </EmblaSlide>
                                    ))}
                                </EmblaSlideFlex>
                            </EmblaViewport>
                        </EmblaContainer>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mx: 2 }}>
                            <PrevNextButton onClick={scrollPrevAi} disableRipple>
                                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                            </PrevNextButton>

                            <Box sx={{ display: "flex", justifyContent: "center", flex: 1 }}>
                                {aiMember.map((_, index) => (
                                    <DotButton key={index} active={index === selectedIndexAi} onClick={() => scrollToAi(index)} />
                                ))}
                            </Box>

                            <PrevNextButton onClick={scrollNextAi} disableRipple>
                                <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                            </PrevNextButton>
                        </Box>
                    </Box>
                )}
            </Container>
        </div>
    );
}

export default Home;
